import { TemplateOpeningRoutes, TemplatesTab } from "@/modules/template/common/enum";
import {
  SelectTransactionTypeModal,
  TemplateNameModal,
  Templates
} from "@/modules/template/components";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage, EmptyOrErrorContent, PageHeader } from "@/shared/components";
import { debounce } from "@/shared/utils";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type ITabTemplate = {
  [key in TemplatesTab]: ReactNode;
};

export const TemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedValue] = useState<string>(TemplatesTab.Transfers);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isOpenSelectTemplateType, setIsOpenSelectTemplateType] = useState<boolean>(false);
  const [isOpenTemplateName, setIsOpenTemplateName] = useState<boolean>(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");

  const updateDebouncedPage = useCallback(
    debounce((value: string) => {
      setSearchValue(value);
    }, 700),
    []
  );

  const onConfirmTransactionTypeSelect = () => {
    setIsOpenTemplateName(true);
    setIsOpenSelectTemplateType(false);
  };

  const onCancelTransactionTypeSelect = () => {
    setIsOpenSelectTemplateType(false);
    setSelectedTransactionType("");
  };

  const onConfirmTemplateNameCreation = (name: string) => {
    setIsOpenTemplateName(false);
    navigate(`${TemplateOpeningRoutes[selectedTransactionType]}?templateName=${name}`);
  };

  const onCancelTemplateNameCreation = () => {
    setIsOpenTemplateName(false);
    setSelectedTransactionType("");
    setTemplateName("");
  };

  const template: ITabTemplate = {
    [TemplatesTab.Transfers]: <Templates templateName={searchValue}/>,
    // TODO: temporary solution
    [TemplatesTab.Payments]: <div
      className="full-width flexbox align-items--center pt-32 pb-32 empty-content-wrapper">
      <EmptyOrErrorContent
        isShowButton={false}
        title={t("general.comingSoon")}
      />
    </div>,
    [TemplatesTab.Groups]: <div
      className="full-width flexbox align-items--center pt-32 pb-32 empty-content-wrapper">
      <EmptyOrErrorContent
        isShowButton={false}
        title={t("general.comingSoon")}
      />
    </div>,
  };

  return (
    <div className="full-width page-container templates-page">
      <PageHeader>
        <div className="flexbox">
          <Input
            leftIconProps={{
              Component: (props) => (
                <CloudFlareImage {...props} path={CloudflareAsset.Search}/>
              )
            }}
            onChange={(e) => updateDebouncedPage(e.target.value)}
            placeholder={t("template.search.placeholder")}
            className="mr-16 header-search-input"
          />
          <Button
            buttonText={t("template.newTemplate.title")}
            data-testid="add-template"
            data-ga-id="add-template"
            data-ga-action="Open select template dialog"
            onClick={() => setIsOpenSelectTemplateType(true)}
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage {...props} path={CloudflareAsset.AddLight}/>
              ),
            }}
          />
        </div>
      </PageHeader>
      <SelectTransactionTypeModal
        isOpen={isOpenSelectTemplateType}
        onClose={onCancelTransactionTypeSelect}
        onConfirm={onConfirmTransactionTypeSelect}
        selectedTransactionType={selectedTransactionType}
        setSelectedTransactionType={setSelectedTransactionType}
      />
      <TemplateNameModal
        isOpen={isOpenTemplateName}
        onClose={onCancelTemplateNameCreation}
        onConfirm={onConfirmTemplateNameCreation}
        templateName={templateName}
        setTemplateName={setTemplateName}
      />
      <div className="full-width flexbox justify-content--center h-fit-content page-body">
        <div className="templates-wrapper">
          <Heading className="content-title mb-20" size="xsmall" weight="bolder">
            {t("templates.title")}
          </Heading>
          <Text
            weight="bold"
            type="secondary"
            className="mb-16"
          >
            {t("transactions.title")}
          </Text>
          {/*TODO: temporary solution*/}
          {/*<div*/}
          {/*  className="flexbox justify-content--between align-items--center full-width tabs-section mt-16 mb-32">*/}
          {/*  <Tab*/}
          {/*    size="small"*/}
          {/*    color="light"*/}
          {/*    tabItems={getTemplateTabItems({ t })}*/}
          {/*    selectedValue={selectedValue}*/}
          {/*    onSelect={(value) => setSelectedValue(value as string)}*/}
          {/*  />*/}
          {/*</div>*/}
          <div className="full-width widget-wrapper">
            {template[selectedValue as TemplatesTab]}
          </div>
        </div>
      </div>
    </div>
  );
};
